import { useCallback, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import { formatFileSize, MAX_PDF_FILE_SIZE, validatePdfFileSize } from "@/hooks/uploads/uploadValidation";
import { uploadFileToStorage } from "@/lib/uploadFileToStorage";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadPdfProps extends UploadComponentProps {
  pdfFile: File | null;
  onPdfFileChange: (file: File | null) => void;
}

const UploadPdf = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  pdfFile,
  onPdfFileChange,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadPdfProps) => {
  const uploadPdfMutation = useUploadMutation("pdf", selectedAgentId, onUploadSuccess);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);

  const notifyPdfTooLarge = useCallback(() => {
    toast.error(`PDF must be smaller than ${formatFileSize(MAX_PDF_FILE_SIZE)}`, {
      description: "Compress it for free, then upload again.",
      duration: 15000,
      action: {
        label: "Compress PDF",
        onClick: () => {
          const link = document.createElement("a");
          link.href = "https://www.ilovepdf.com/compress_pdf?utm_source=helpgenie.ai";
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.click();
        },
      },
    });
  }, []);

  const uploadPdfFile = useCallback(
    async (file: File) => {
      try {
        setIsUploadingPdf(true);
        const uploadToast = toast.loading(`Uploading "${file.name}"...`);

        const { fileUrl } = await uploadFileToStorage(file, "temp-uploads");

        toast.dismiss(uploadToast);

        onDeferredActionAdd({
          type: "upload-file",
          data: {
            fileUrl,
            fileName: file.name,
          },
        });
        toast.success(`"${file.name}" uploaded and queued for agent linking`);
        onPdfFileChange(null);
      } catch (error) {
        toast.error(`Failed to upload "${file.name}"`);
        console.error("Upload error:", error);
      } finally {
        setIsUploadingPdf(false);
      }
    },
    [onDeferredActionAdd, onPdfFileChange],
  );

  const handlePdfFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.type !== "application/pdf") {
        toast.error("Please select a PDF file");
        return;
      }

      if (!validatePdfFileSize(file)) {
        notifyPdfTooLarge();
        return;
      }

      if (onDeferredActionAdd) {
        await uploadPdfFile(file);
        e.target.value = "";
      } else {
        onPdfFileChange(file);
      }
    },
    [onPdfFileChange, onDeferredActionAdd, uploadPdfFile, notifyPdfTooLarge],
  );

  const handlePdfDrop = useCallback(
    async (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (!file) return;

      if (file.type !== "application/pdf") {
        toast.error("Please select a PDF file");
        return;
      }

      if (!validatePdfFileSize(file)) {
        notifyPdfTooLarge();
        return;
      }

      if (onDeferredActionAdd) {
        await uploadPdfFile(file);
      } else {
        onPdfFileChange(file);
      }
    },
    [onPdfFileChange, onDeferredActionAdd, uploadPdfFile, notifyPdfTooLarge],
  );

  const handlePdfDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon name="document" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Upload PDF Document</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Select a PDF file from your computer to add to your knowledge base
        </p>

        <input
          type="file"
          accept=".pdf"
          onChange={handlePdfFileChange}
          className="hidden"
          id="pdf-file-input"
        />
        <label
          htmlFor="pdf-file-input"
          onDrop={handlePdfDrop}
          onDragOver={handlePdfDragOver}
          className="flex items-center justify-center w-full max-w-md h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-genie-primary transition-colors bg-gray-50"
        >
          {pdfFile ? (
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">{pdfFile.name}</p>
              <p className="text-xs text-gray-500 mt-1">{formatFileSize(pdfFile.size)}</p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-gray-600">Click or drag to upload PDF</p>
              <p className="text-xs text-gray-500 mt-1">PDF files only</p>
            </div>
          )}
        </label>
        {pdfFile && !onDeferredActionAdd && (
          <Button
            onClick={() => uploadPdfMutation.mutate({ file: pdfFile })}
            disabled={!pdfFile || uploadPdfMutation.isPending}
            className="mt-4 bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-8 h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
          >
            {uploadPdfMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload PDF"
            )}
          </Button>
        )}
      </div>
    </>
  );
};

export default UploadPdf;
